import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import './CountryImages.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
const SERVER_URL = API_BASE.replace('/api', '');

// Generate flag emoji from country code
const getFlagEmoji = (countryCode) => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

function CountryImages() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadingCountry, setUploadingCountry] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const fetchCountries = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/countries`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setCountries(data.data.countries);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      showToast('Failed to load countries', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImageUpload = async (countryCode, file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showToast('Only JPEG, PNG, and WebP images are allowed', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be less than 5MB', 'error');
      return;
    }

    setUploadingCountry(countryCode);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE}/countries/${countryCode}/image`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        showToast('Image uploaded successfully');
        fetchCountries();
      } else {
        showToast(data.message || 'Failed to upload image', 'error');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Failed to upload image', 'error');
    } finally {
      setUploadingCountry(null);
    }
  };

  const handleDeleteImage = async (countryCode) => {
    setConfirmModal({
      show: true,
      title: 'Delete Image',
      message: 'Are you sure you want to delete this country image? This action cannot be undone.',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_BASE}/countries/${countryCode}/image`, {
            method: 'DELETE',
            credentials: 'include'
          });

          const data = await response.json();

          if (data.success) {
            showToast('Image deleted successfully');
            fetchCountries();
          } else {
            showToast(data.message || 'Failed to delete image', 'error');
          }
        } catch (error) {
          console.error('Error deleting image:', error);
          showToast('Failed to delete image', 'error');
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterStatus === 'with_image') return matchesSearch && country.image_url;
    if (filterStatus === 'without_image') return matchesSearch && !country.image_url;
    return matchesSearch;
  });

  const getFilterLabel = () => {
    switch (filterStatus) {
      case 'with_image': return 'With Image';
      case 'without_image': return 'Without Image';
      default: return 'All Countries';
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Country Images</h1>
            <p className="header-subtitle">Upload banner images for countries displayed on user panel</p>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Search and Filter Bar */}
          <div className="countries-toolbar">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
              </svg>
              <input
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="toolbar-actions">
              <div className="custom-dropdown" ref={filterDropdownRef}>
                <button
                  className="custom-dropdown-trigger"
                  onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                >
                  {getFilterLabel()}
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {filterDropdownOpen && (
                  <div className="custom-dropdown-menu">
                    <div
                      className={`custom-dropdown-item ${filterStatus === 'all' ? 'active' : ''}`}
                      onClick={() => { setFilterStatus('all'); setFilterDropdownOpen(false); }}
                    >
                      All Countries
                    </div>
                    <div
                      className={`custom-dropdown-item ${filterStatus === 'with_image' ? 'active' : ''}`}
                      onClick={() => { setFilterStatus('with_image'); setFilterDropdownOpen(false); }}
                    >
                      With Image
                    </div>
                    <div
                      className={`custom-dropdown-item ${filterStatus === 'without_image' ? 'active' : ''}`}
                      onClick={() => { setFilterStatus('without_image'); setFilterDropdownOpen(false); }}
                    >
                      Without Image
                    </div>
                  </div>
                )}
              </div>
              <span className="countries-count">
                {filteredCountries.length} of {countries.length} countries
              </span>
            </div>
          </div>

          {/* Countries Grid */}
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Loading countries...</span>
            </div>
          ) : filteredCountries.length === 0 ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              <span>No countries found</span>
            </div>
          ) : (
            <div className="countries-grid">
              {filteredCountries.map(country => (
                <CountryCard
                  key={country.id}
                  country={country}
                  uploading={uploadingCountry === country.code}
                  onUpload={handleImageUpload}
                  onDelete={handleDeleteImage}
                />
              ))}
            </div>
          )}
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {confirmModal.show && (
          <ConfirmModal
            title={confirmModal.title}
            message={confirmModal.message}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal({ show: false })}
          />
        )}
      </main>
    </div>
  );
}

function CountryCard({ country, uploading, onUpload, onDelete }) {
  const fileInputRef = React.useRef(null);
  const flagEmoji = getFlagEmoji(country.code);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(country.code, file);
    }
    e.target.value = '';
  };

  return (
    <div className="country-card">
      <div className="country-card-image">
        {country.image_url ? (
          <img
            src={`${SERVER_URL}${country.image_url}`}
            alt={country.name}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="country-card-placeholder" style={{ display: country.image_url ? 'none' : 'flex' }}>
          <span className="placeholder-emoji">{flagEmoji}</span>
          <span className="placeholder-text">No Image</span>
        </div>
        {uploading && (
          <div className="upload-overlay">
            <div className="spinner-small"></div>
          </div>
        )}
        {country.image_url && (
          <span className="image-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </span>
        )}
      </div>
      <div className="country-card-info">
        <div className="country-card-header">
          <span className="country-flag">{flagEmoji}</span>
          <div className="country-details">
            <span className="country-name">{country.name}</span>
            <span className="country-code">{country.code}</span>
          </div>
        </div>
        <div className="country-card-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            className="btn-card btn-card-primary"
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
          >
            {country.image_url ? 'Replace' : 'Upload'}
          </button>
          {country.image_url && (
            <button
              className="btn-card btn-card-danger"
              onClick={() => onDelete(country.code)}
              disabled={uploading}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CountryImages;
